import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Plug,
  Route as RouteIcon,
  Wrench,
  Server,
  Copy,
  Check,
  Eye,
  X,
  AlertCircle,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, getErrorMessage } from '../lib/api';
import { toast, alert } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui';
import type { KongProductSyncSummary, KongConfiguration } from '../types';

interface VerifyProductResult {
  product_id: string;
  product_name: string;
  in_sync: boolean;
  missing_in_kong: string[];
  extra_in_kong: string[];
  drift_detected: boolean;
}

interface ProductDetailData {
  product: {
    id: string;
    name: string;
    published: boolean;
  };
  summary: {
    total: number;
    synced: number;
    failed: number;
    pending: number;
    deleted: number;
  };
  configurations: KongConfiguration[];
}

export const KongClusterSync: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'synced' | 'drift_failed' | 'pending'>('all');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [driftReport, setDriftReport] = useState<Record<string, VerifyProductResult>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // 1. Fetch Cluster Sync Status
  const {
    data: productsSummary = [],
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<KongProductSyncSummary[]>({
    queryKey: ['kong-cluster-sync-status'],
    queryFn: async () => {
      const response = await api.get<{ data: KongProductSyncSummary[] }>(
        '/portal/admin/kong/sync-status'
      );
      return response.data?.data || [];
    },
  });

  // 2. Fetch Selected Product Deep Inspection Data
  const { data: inspectedDetail, isLoading: isLoadingDetail } = useQuery<ProductDetailData | null>({
    queryKey: ['kong-product-detail', selectedProductId],
    queryFn: async () => {
      if (!selectedProductId) return null;
      const response = await api.get<{ data: ProductDetailData }>(
        `/portal/admin/kong/sync-status/${selectedProductId}`
      );
      return response.data?.data || null;
    },
    enabled: !!selectedProductId,
  });

  // 3. Cluster Verify All Mutation
  const verifyAllMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        data: {
          total: number;
          in_sync: number;
          drift_detected: boolean;
          drift_count: number;
          results: VerifyProductResult[];
        };
        message: string;
      }>('/portal/admin/kong/verify-all');
      return response.data;
    },
    onSuccess: (res) => {
      const newReport: Record<string, VerifyProductResult> = {};
      res.data.results.forEach((r) => {
        newReport[r.product_id] = r;
      });
      setDriftReport(newReport);

      if (res.data.drift_detected) {
        void alert.warning(
          'Divergência Detetada no Cluster Kong',
          `Foram encontradas discrepâncias em ${res.data.drift_count} produto(s). Verifique os produtos assinalados e sincronize.`
        );
      } else {
        void alert.success(
          'Cluster Kong em Conformidade',
          'Todas as rotas, serviços e plugins estão 100% sincronizados com a base de dados.'
        );
      }
      void refetch();
    },
    onError: (err) => {
      toast.error(`Falha ao verificar deriva: ${getErrorMessage(err)}`);
    },
  });

  // 4. Cluster Sync All Mutation
  const syncAllMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        data: {
          total: number;
          synced: number;
          failed: number;
        };
        message: string;
      }>('/portal/admin/kong/sync-all');
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || 'Sincronização do cluster concluída com sucesso!');
      void queryClient.invalidateQueries({ queryKey: ['kong-cluster-sync-status'] });
      if (selectedProductId) {
        void queryClient.invalidateQueries({ queryKey: ['kong-product-detail', selectedProductId] });
      }
    },
    onError: (err) => {
      toast.error(`Falha ao sincronizar cluster: ${getErrorMessage(err)}`);
    },
  });

  // 5. Individual Product Sync Mutation
  const syncProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      const response = await api.post(`/portal/admin/kong/sync/${productId}`);
      return response.data;
    },
    onSuccess: (_, productId) => {
      toast.success('Produto sincronizado com o Kong com sucesso!');
      setDriftReport((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['kong-cluster-sync-status'] });
      if (selectedProductId === productId) {
        void queryClient.invalidateQueries({ queryKey: ['kong-product-detail', productId] });
      }
    },
    onError: (err) => {
      toast.error(`Falha na sincronização do produto: ${getErrorMessage(err)}`);
    },
  });

  // 6. Individual Product Verify Mutation
  const verifyProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      const response = await api.post<{
        data: VerifyProductResult;
      }>('/portal/admin/kong/verify', { productId });
      return response.data;
    },
    onSuccess: (res, productId) => {
      const r = res.data;
      setDriftReport((prev) => ({ ...prev, [productId]: r }));
      if (r.drift_detected) {
        const issues = [...(r.missing_in_kong || []), ...(r.extra_in_kong || [])].join(', ');
        void alert.warning(
          'Divergência no Produto',
          `Discrepâncias detetadas: ${issues || 'Configurações ausentes no Kong'}. Clique em Sincronizar para resolver.`
        );
      } else {
        toast.success(`Configuração de "${r.product_name}" 100% sincronizada com o Kong.`);
      }
    },
    onError: (err) => {
      toast.error(`Falha ao verificar produto: ${getErrorMessage(err)}`);
    },
  });

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    toast.success('Copiado para a área de transferência');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Metrics computation
  const totalProducts = productsSummary.length;
  const totalKongConfigs = productsSummary.reduce((acc, p) => acc + Number(p.total_configs || 0), 0);
  const totalSyncedConfigs = productsSummary.reduce((acc, p) => acc + Number(p.synced_count || 0), 0);
  const totalFailedConfigs = productsSummary.reduce((acc, p) => acc + Number(p.failed_count || 0), 0);
  const totalPendingConfigs = productsSummary.reduce((acc, p) => acc + Number(p.pending_count || 0), 0);

  const productsWithDrift = Object.values(driftReport).filter((r) => r.drift_detected).length;
  const hasClusterIssues = totalFailedConfigs > 0 || productsWithDrift > 0;

  // Filtered products
  const filteredProducts = productsSummary.filter((p) => {
    const matchesSearch =
      p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.product_id.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    const hasFailed = Number(p.failed_count) > 0;
    const hasPending = Number(p.pending_count) > 0;
    const isDrifting = driftReport[p.product_id]?.drift_detected;

    if (statusFilter === 'synced') {
      return !hasFailed && !hasPending && !isDrifting && Number(p.total_configs) > 0;
    }
    if (statusFilter === 'drift_failed') {
      return hasFailed || isDrifting;
    }
    if (statusFilter === 'pending') {
      return hasPending;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs>
        <BreadcrumbItem href="/">Dashboard</BreadcrumbItem>
        <BreadcrumbItem href="/integration-routes">Gateway & Integração</BreadcrumbItem>
        <BreadcrumbItem>Sincronização Kong</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Server className="w-7 h-7 text-primary-500" />
            Sincronização & Cluster Kong
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Monitorização declarativa, deteção de deriva de estado e sincronização de rotas, serviços e plugins no Kong Gateway.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => refetch()}
            disabled={isRefetching || isLoading}
            className="btn-secondary inline-flex items-center gap-2"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>

          <button
            onClick={() => verifyAllMutation.mutate()}
            disabled={verifyAllMutation.isPending || isLoading}
            className="btn-secondary inline-flex items-center gap-2 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
          >
            <Search className={`w-4 h-4 ${verifyAllMutation.isPending ? 'animate-spin' : ''}`} />
            <span>{verifyAllMutation.isPending ? 'A verificar deriva...' : 'Verificar Deriva do Cluster'}</span>
          </button>

          <button
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending || isLoading}
            className="btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncAllMutation.isPending ? 'animate-spin' : ''}`} />
            <span>{syncAllMutation.isPending ? 'A sincronizar cluster...' : 'Sincronizar Cluster Completo'}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Produtos Ativos
            </span>
            <Layers className="w-5 h-5 text-primary-500" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{totalProducts}</div>
          <p className="text-xs text-slate-500 mt-1">Produtos no catálogo</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total de Entidades
            </span>
            <Server className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{totalKongConfigs}</div>
          <p className="text-xs text-slate-500 mt-1">Serviços, rotas & plugins</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Sincronizadas
            </span>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{totalSyncedConfigs}</div>
          <p className="text-xs text-slate-500 mt-1">
            {totalKongConfigs > 0
              ? `${Math.round((totalSyncedConfigs / totalKongConfigs) * 100)}% de conformidade`
              : 'Sem configurações'}
          </p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Pendentes
            </span>
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{totalPendingConfigs}</div>
          <p className="text-xs text-slate-500 mt-1">Aguardam envio ao Kong</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Falhas / Deriva
            </span>
            <AlertTriangle className={`w-5 h-5 ${hasClusterIssues ? 'text-rose-500 animate-pulse' : 'text-slate-400'}`} />
          </div>
          <div className={`text-2xl font-bold ${hasClusterIssues ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
            {totalFailedConfigs + productsWithDrift}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {totalFailedConfigs} falhas, {productsWithDrift} deriva
          </p>
        </div>
      </div>

      {/* Cluster Issue Banner */}
      {hasClusterIssues && (
        <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                Discrepâncias de Configuração no Cluster Kong
              </p>
              <p className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                Existem entidades com falha de sincronização ou divergência entre o estado da base de dados e o Kong. Execute &quot;Sincronizar Cluster Completo&quot; para restabelecer a integridade.
              </p>
            </div>
          </div>
          <button
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending}
            className="btn-primary text-xs shrink-0 whitespace-nowrap bg-rose-600 hover:bg-rose-700 text-white border-0 shadow-sm"
          >
            Sincronizar Cluster Agora
          </button>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="card p-4 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Pesquisar por nome ou ID do produto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9 w-full text-sm"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Filtrar por:</span>
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === 'all'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              Todos ({productsSummary.length})
            </button>
            <button
              onClick={() => setStatusFilter('synced')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === 'synced'
                  ? 'bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              Sincronizados
            </button>
            <button
              onClick={() => setStatusFilter('drift_failed')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === 'drift_failed'
                  ? 'bg-white dark:bg-slate-900 text-rose-600 dark:text-rose-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              Deriva / Falha
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === 'pending'
                  ? 'bg-white dark:bg-slate-900 text-amber-600 dark:text-amber-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              Pendentes
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-6 py-3.5">Produto</th>
                <th className="px-6 py-3.5">Estado do Kong</th>
                <th className="px-6 py-3.5">Entidades (S / P / F)</th>
                <th className="px-6 py-3.5">Última Sincronização</th>
                <th className="px-6 py-3.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-primary-500" />
                    <span>A carregar estado do cluster Kong...</span>
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    Nenhum produto encontrado com os filtros atuais.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p) => {
                  const total = Number(p.total_configs || 0);
                  const synced = Number(p.synced_count || 0);
                  const failed = Number(p.failed_count || 0);
                  const pending = Number(p.pending_count || 0);
                  const driftInfo = driftReport[p.product_id];
                  const isDrifting = driftInfo?.drift_detected;

                  return (
                    <tr
                      key={p.product_id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      {/* Product Name & ID */}
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                          {p.product_name}
                          <Link
                            to={`/products/${p.product_id}?tab=kong`}
                            className="text-slate-400 hover:text-primary-500"
                            title="Ir para Configuração do Produto"
                          >
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                        <div className="text-xs font-mono text-slate-400 dark:text-slate-500 mt-0.5">
                          {p.product_id}
                        </div>
                      </td>

                      {/* Kong Status Badge */}
                      <td className="px-6 py-4">
                        {isDrifting ? (
                          <span className="badge badge-warning inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                            Deriva Detetada
                          </span>
                        ) : failed > 0 ? (
                          <span className="badge badge-danger inline-flex items-center gap-1">
                            <XCircle className="w-3 h-3 text-rose-500" />
                            {failed} Falha(s)
                          </span>
                        ) : pending > 0 ? (
                          <span className="badge badge-warning inline-flex items-center gap-1">
                            <Clock className="w-3 h-3 text-amber-500" />
                            Pendente
                          </span>
                        ) : total > 0 && synced === total ? (
                          <span className="badge badge-success inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                            Sincronizado
                          </span>
                        ) : (
                          <span className="badge badge-gray">Não Configurado</span>
                        )}
                      </td>

                      {/* Entities Count */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-xs font-mono">
                          <span className="text-slate-900 dark:text-white font-semibold">
                            {total} total
                          </span>
                          <span className="text-slate-300 dark:text-slate-600">|</span>
                          <span className="text-emerald-600 dark:text-emerald-400" title="Sincronizadas">
                            {synced} S
                          </span>
                          <span className="text-slate-300 dark:text-slate-600">|</span>
                          <span className="text-amber-600 dark:text-amber-400" title="Pendentes">
                            {pending} P
                          </span>
                          <span className="text-slate-300 dark:text-slate-600">|</span>
                          <span className="text-rose-600 dark:text-rose-400" title="Falhas">
                            {failed} F
                          </span>
                        </div>
                      </td>

                      {/* Last Synced At */}
                      <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                        {p.last_synced_at ? (
                          new Date(p.last_synced_at).toLocaleString('pt-PT')
                        ) : (
                          <span className="text-slate-400 italic">Nunca sincronizado</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedProductId(p.product_id)}
                            className="btn-secondary py-1 px-2.5 text-xs inline-flex items-center gap-1"
                            title="Inspecionar entidades Kong"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Inspecionar</span>
                          </button>

                          <button
                            onClick={() => verifyProductMutation.mutate(p.product_id)}
                            disabled={verifyProductMutation.isPending}
                            className="btn-secondary py-1 px-2.5 text-xs inline-flex items-center gap-1 text-indigo-700 dark:text-indigo-300"
                            title="Verificar deriva contra o Kong"
                          >
                            <Search className="w-3.5 h-3.5" />
                            <span>Verificar</span>
                          </button>

                          <button
                            onClick={() => syncProductMutation.mutate(p.product_id)}
                            disabled={syncProductMutation.isPending}
                            className="btn-primary py-1 px-2.5 text-xs inline-flex items-center gap-1"
                            title="Sincronizar com o Kong"
                          >
                            <RefreshCw
                              className={`w-3.5 h-3.5 ${
                                syncProductMutation.isPending &&
                                syncProductMutation.variables === p.product_id
                                  ? 'animate-spin'
                                  : ''
                              }`}
                            />
                            <span>Sincronizar</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deep Inspection Modal */}
      {selectedProductId && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-4xl w-full p-6 shadow-2xl my-8 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    Inspeção de Entidades Kong
                  </h3>
                  {inspectedDetail?.product && (
                    <span className="badge badge-primary">{inspectedDetail.product.name}</span>
                  )}
                </div>
                <p className="text-xs font-mono text-slate-400 mt-1">ID: {selectedProductId}</p>
              </div>
              <button
                onClick={() => setSelectedProductId(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              {isLoadingDetail ? (
                <div className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-primary-500" />
                  <span>A carregar detalhes das entidades Kong...</span>
                </div>
              ) : !inspectedDetail ? (
                <div className="py-8 text-center text-slate-500">
                  Não foi possível carregar as informações do produto.
                </div>
              ) : (
                <>
                  {/* Summary Bar */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                      <p className="text-xs text-slate-500">Total de Entidades</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">
                        {inspectedDetail.summary.total}
                      </p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800/40">
                      <p className="text-xs text-emerald-700 dark:text-emerald-300">Sincronizadas</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {inspectedDetail.summary.synced}
                      </p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-xl border border-amber-200 dark:border-amber-800/40">
                      <p className="text-xs text-amber-700 dark:text-amber-300">Pendentes</p>
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        {inspectedDetail.summary.pending}
                      </p>
                    </div>
                    <div className="bg-rose-50 dark:bg-rose-950/30 p-3 rounded-xl border border-rose-200 dark:border-rose-800/40">
                      <p className="text-xs text-rose-700 dark:text-rose-300">Falhas</p>
                      <p className="text-lg font-bold text-rose-600 dark:text-rose-400">
                        {inspectedDetail.summary.failed}
                      </p>
                    </div>
                  </div>

                  {/* Drift Alert for this product if detected */}
                  {driftReport[selectedProductId]?.drift_detected && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-900 dark:text-amber-200">
                      <p className="font-semibold flex items-center gap-1.5 mb-1">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        Divergência detetada na verificação de deriva:
                      </p>
                      {driftReport[selectedProductId].missing_in_kong.length > 0 && (
                        <p>
                          Ausentes no Kong:{' '}
                          <code className="font-mono">
                            {driftReport[selectedProductId].missing_in_kong.join(', ')}
                          </code>
                        </p>
                      )}
                      {driftReport[selectedProductId].extra_in_kong.length > 0 && (
                        <p>
                          Extras no Kong:{' '}
                          <code className="font-mono">
                            {driftReport[selectedProductId].extra_in_kong.join(', ')}
                          </code>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Entities List */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider text-xs">
                      Entidades Declaradas no Kong ({inspectedDetail.configurations.length})
                    </h4>

                    {inspectedDetail.configurations.length === 0 ? (
                      <p className="text-sm text-slate-500 italic py-4">
                        Nenhuma entidade registada. Clique em &quot;Sincronizar Produto&quot; para registar o serviço, rotas e plugins no Kong.
                      </p>
                    ) : (
                      inspectedDetail.configurations.map((config) => {
                        const icon =
                          config.entity_type === 'SERVICE' ? (
                            <Plug className="w-4 h-4 text-primary-500" />
                          ) : config.entity_type === 'ROUTE' ? (
                            <RouteIcon className="w-4 h-4 text-indigo-500" />
                          ) : (
                            <Wrench className="w-4 h-4 text-amber-500" />
                          );

                        return (
                          <div
                            key={config.id}
                            className="border border-slate-200 dark:border-slate-800 rounded-xl p-3.5 bg-slate-50/50 dark:bg-slate-800/30"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                {icon}
                                <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">
                                  {config.entity_name || config.entity_id || 'Sem nome'}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded font-mono font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                  {config.entity_type}
                                </span>
                                {config.endpoint_name && (
                                  <span className="text-xs text-slate-500">
                                    ({config.endpoint_name})
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <span
                                  className={`badge ${
                                    config.sync_status === 'SYNCED'
                                      ? 'badge-success'
                                      : config.sync_status === 'FAILED'
                                      ? 'badge-danger'
                                      : 'badge-warning'
                                  }`}
                                >
                                  {config.sync_status}
                                </span>
                              </div>
                            </div>

                            {/* Kong ID & Synced Timestamp */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mb-2">
                              {config.entity_id && (
                                <div className="flex items-center gap-1">
                                  <span>Kong ID:</span>
                                  <code className="font-mono bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                                    {config.entity_id}
                                  </code>
                                  <button
                                    onClick={() => handleCopy(config.entity_id || '', config.id)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                    title="Copiar Kong ID"
                                  >
                                    {copiedKey === config.id ? (
                                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              )}

                              {config.last_synced_at && (
                                <span>
                                  Sincronizado em:{' '}
                                  {new Date(config.last_synced_at).toLocaleString('pt-PT')}
                                </span>
                              )}
                            </div>

                            {/* Sync Error if failed */}
                            {config.sync_error && (
                              <div className="p-2.5 mb-2 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg text-xs text-rose-800 dark:text-rose-300">
                                <span className="font-semibold">Erro:</span> {config.sync_error}
                                <span className="ml-2 text-rose-500">
                                  (Tentativas: {config.sync_attempts})
                                </span>
                              </div>
                            )}

                            {/* Expandable Technical JSON */}
                            <details className="mt-2 text-xs">
                              <summary className="cursor-pointer text-primary-600 dark:text-primary-400 hover:underline select-none">
                                Ver configuração técnica JSON
                              </summary>
                              <pre className="mt-2 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg font-mono text-xs overflow-x-auto max-h-48 text-slate-800 dark:text-slate-200">
                                {JSON.stringify(config.configuration, null, 2)}
                              </pre>
                            </details>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <button onClick={() => setSelectedProductId(null)} className="btn-secondary">
                Fechar
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => verifyProductMutation.mutate(selectedProductId)}
                  disabled={verifyProductMutation.isPending}
                  className="btn-secondary inline-flex items-center gap-1.5"
                >
                  <Search className="w-4 h-4" />
                  <span>Verificar Deriva</span>
                </button>
                <button
                  onClick={() => syncProductMutation.mutate(selectedProductId)}
                  disabled={syncProductMutation.isPending}
                  className="btn-primary inline-flex items-center gap-1.5"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${syncProductMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  <span>Sincronizar Produto</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
